import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useHttpClient } from '@bonusly/http-client';

// Path helpers
export const bizyChatPath = (prefix = '') => `${prefix}/bizy/chat`;
export const bizyFeedbackPath = (messageId: string, prefix = '') => `${prefix}/bizy/feedback/${messageId}`;
export const bizyTitlePath = (prefix = '') => `${prefix}/bizy/title`;

// Streaming API Types
export interface BizyChatV2Request {
	message: string;
	conversation_id?: string;
	context?: Record<string, string>;
}

export interface BizyChatV2Response {
	success: boolean;
	conversation_id: string;
	request_id?: string;
}

/**
 * Hook for sending chat messages to Bizy (streaming).
 *
 * POSTs to /api/v2/bizy/chat which enqueues a background job and returns
 * 202 immediately. The response is streamed via ActionCable (BizyChannel).
 */
export const useBizyChatV2Mutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (params: BizyChatV2Request): Promise<BizyChatV2Response> => {
			const response = await http.post<BizyChatV2Response>(bizyChatPath(), params);
			return response.data;
		},
	});
};

// Cancel API
export const bizyChatCancelPath = (prefix = '') => `${prefix}/bizy/chat/cancel`;

export const useBizyCancelMutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (params: { request_id: string; conversation_id?: string }) => {
			const response = await http.post(bizyChatCancelPath(), params);
			return response.data;
		},
	});
};

// Feedback API Types
export interface BizyFeedbackRequest {
	messageId: string;
	feedback_type: 'positive' | 'negative' | null;
	feedback_category?: string;
	feedback_details?: string;
}

export interface BizyFeedbackResponse {
	success: boolean;
	error?: string;
}

/**
 * Hook for submitting feedback on a Bizy response.
 *
 * PATCH /api/v2/bizy/feedback/:id
 */
export const useBizyFeedbackMutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async ({ messageId, ...data }: BizyFeedbackRequest): Promise<BizyFeedbackResponse> => {
			const response = await http.patch<BizyFeedbackResponse>(bizyFeedbackPath(messageId), data);
			return response.data;
		},
	});
};

// Title Generation API Types
export interface BizyTitleRequest {
	message: string;
}

export interface BizyTitleResponse {
	title: string;
}

/**
 * Hook for generating an AI conversation title from a user message.
 *
 * POST /api/v2/bizy/title — synchronous, no streaming, no persistence.
 */
export const useBizyTitleMutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (params: BizyTitleRequest): Promise<BizyTitleResponse> => {
			const response = await http.post<BizyTitleResponse>(bizyTitlePath(), params);
			return response.data;
		},
	});
};

// Activation State API
export const bizyActivationStatePath = (prefix = '') => `${prefix}/bizy/activation_state`;
export const bizyPromptSuggestionsPath = (prefix = '') => `${prefix}/bizy/prompt_suggestions`;

export interface ActivationStateUserSignals {
	recognition_given_ever: boolean;
	redemption_made_ever: boolean;
	recognition_given_last_30_days: boolean;
	recognition_received_ever: boolean;
	recognition_received_last_30_days: boolean;
	has_active_one_on_one: boolean;
	has_completed_one_on_one: boolean;
	completed_one_on_one_last_30_days: boolean;
}

export interface ActivationStateAdminSignals {
	activated: boolean;
	activated_at: string | null;
	company_user_count: number;
	company_active_user_count: number;
	company_recognition_count: number;
	company_one_on_one_count: number;
	completed_one_on_one_count_last_30_days: number;
}

export interface ActivationStateCompanySignals {
	days_since_signup: number | null;
}

export interface ActivationStateResponse {
	user: ActivationStateUserSignals;
	company: ActivationStateCompanySignals;
	admin?: ActivationStateAdminSignals;
}

export const bizyActivationStateQueryKey = () => ['bizy-activation-state'];

export const useBizyActivationState = (enabled = true) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyActivationStateQueryKey(),
		queryFn: async (): Promise<ActivationStateResponse> => {
			const response = await http.get<ActivationStateResponse>(bizyActivationStatePath());
			return response.data;
		},
		enabled,
		staleTime: 5 * 60 * 1000,
	});
};

export interface BizyPromptSuggestion {
	id: string;
	prompt: string;
	workflow_name: string;
	workflow_rank: number;
	score: number | null;
	category_id: 'personalized';
	prompt_key: string;
}

export interface BizyPromptSuggestionsResponse {
	prompt_suggestions: BizyPromptSuggestion[];
}

export const bizyPromptSuggestionsQueryKey = () => ['bizy-prompt-suggestions'];

export const BIZY_PROMPT_SUGGESTIONS_STALE_TIME = 5 * 60 * 1000;

type RawPromptSuggestionRow = {
	id: string;
	prompt: string;
	workflow_name?: string;
	workflowName?: string;
	workflow_rank?: number;
	workflowRank?: number;
	score?: number | null;
	prompt_key?: string;
	promptKey?: string;
};

function parsePromptSuggestionsResponse(data: unknown): BizyPromptSuggestion[] {
	if (!data || typeof data !== 'object') return [];

	const record = data as Record<string, unknown>;
	const rows = (record.prompt_suggestions ?? record.promptSuggestions) as RawPromptSuggestionRow[] | undefined;
	if (!Array.isArray(rows)) return [];

	return rows
		.filter((row) => row?.prompt?.trim())
		.map((row) => ({
			id: String(row.id),
			prompt: row.prompt.trim(),
			workflow_name: row.workflow_name ?? row.workflowName ?? '',
			workflow_rank: row.workflow_rank ?? row.workflowRank ?? 0,
			score: row.score ?? null,
			category_id: 'personalized' as const,
			prompt_key: row.prompt_key ?? row.promptKey ?? `personalized:${row.id}`,
		}));
}

export async function fetchBizyPromptSuggestions(http: ReturnType<typeof useHttpClient>): Promise<BizyPromptSuggestionsResponse> {
	const response = await http.get(bizyPromptSuggestionsPath());
	return { prompt_suggestions: parsePromptSuggestionsResponse(response.data) };
}

/** Warm prompt suggestions on page load so the empty state does not flash before Bizy opens. */
export function usePrefetchBizyPromptSuggestions(enabled = true) {
	const queryClient = useQueryClient();
	const http = useHttpClient();

	useEffect(() => {
		if (!enabled) return;
		void queryClient.prefetchQuery({
			queryKey: bizyPromptSuggestionsQueryKey(),
			queryFn: () => fetchBizyPromptSuggestions(http),
			staleTime: BIZY_PROMPT_SUGGESTIONS_STALE_TIME,
		});
	}, [enabled, http, queryClient]);
}

export const useBizyPromptSuggestions = (enabled = true) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyPromptSuggestionsQueryKey(),
		queryFn: () => fetchBizyPromptSuggestions(http),
		enabled,
		staleTime: BIZY_PROMPT_SUGGESTIONS_STALE_TIME,
		refetchOnMount: 'always',
	});
};
